import {checkForAllStyles, StyleCheckerExistence} from '../helpers/styleHelpers';
import {findNodeById, findParentOfNodeById} from '../AstSearchers';
import {AstView, CSSProperty, EnrichedAstNode} from '../types';
import {SizeUnit} from '../UnitConversion/Properties';
import {extractUnitAndValue} from '../UnitConversion/utils/extractUnitAndValue';
import {convertCssUnit, createUnitConverter} from '../UnitConversion/utils/unitConversion';

export function absolutePositionSingleChildren(view: AstView, node: EnrichedAstNode, child: EnrichedAstNode) {
    if (!child.mandatoryProperties.includes('position')) {
        child.mandatoryProperties.push(
            ...[
                'position',
                child.calculatedLayoutAbstraction!.verticalAnchor || 'top',
                child.calculatedLayoutAbstraction!.horizontalAnchor || 'left',
            ]
        );
    }

    const convertUnit = createUnitConverter({view, assignNewValueToNode: true, nodeId: child.internalId});

    const {topDistance, bottomDistance, leftDistance, rightDistance} = getAbsDistance(node, child);

    const isBottom = child.inputLayoutAbstraction.verticalAnchor === 'bottom';
    const isTop = !isBottom;
    const isRight = child.inputLayoutAbstraction.horizontalAnchor === 'right';
    const isLeft = !isRight;

    child.styles['position'] = 'absolute';

    if (child.calculatedLayoutAbstraction!.verticalAnchor === 'bottom') {
        delete child.styles['top'];
        child.styles['bottom'] = bottomDistance;
    } else {
        delete child.styles['bottom'];
        child.styles['top'] = topDistance;
    }

    if (child.calculatedLayoutAbstraction!.horizontalAnchor === 'right') {
        delete child.styles['left'];
        child.styles['right'] = rightDistance;
    } else {
        delete child.styles['right'];
        child.styles['left'] = leftDistance;
    }

    convertUnit({
        key: isTop ? 'top' : 'bottom',
        toUnit: isTop
            ? child.inputLayoutAbstraction.units?.top || 'px'
            : child.inputLayoutAbstraction.units?.bottom || 'px',
    });

    convertUnit({
        key: isLeft ? 'left' : 'right',
        toUnit: isLeft
            ? child.inputLayoutAbstraction.units?.left || 'px'
            : child.inputLayoutAbstraction.units?.right || 'px',
    });

    child.position.marginLeft = 0;
    child.position.marginRight = 0;
    child.position.marginTop = 0;
    child.position.marginBottom = 0;
    delete child.styles['margin'];
    delete child.styles.alignSelf;
}

export function getAbsDistance(parent: EnrichedAstNode | undefined, node: EnrichedAstNode) {
    if (!parent) {
        return {topDistance: 0, leftDistance: 0, bottomDistance: 0, rightDistance: 0};
    }

    const topDistance = node.position.absoluteY - parent.position.absoluteY;
    const leftDistance = node.position.absoluteX - parent.position.absoluteX;

    const bottomDistance = parent.position.height - (topDistance + node.position.height);

    const rightDistance =
        parent.position.absoluteX + parent.position.width - (node.position.absoluteX + node.position.width);

    return {topDistance, leftDistance, bottomDistance, rightDistance};
}

export function setupAbsolutePositionParent(node: EnrichedAstNode) {
    node.styles['position'] = node.styles['position'] ? node.styles['position'] : 'relative';
}

type Axis = 'x' | 'y';
export type SpacingSide = Axis | 'left' | 'top' | 'right' | 'bottom' | 'all';

export const getSideMappings = (type: 'margin' | 'padding'): Record<SpacingSide, CSSProperty[]> => {
    return {
        all: [`${type}Top`, `${type}Bottom`, `${type}Right`, `${type}Left`],
        x: [`${type}Right`, `${type}Left`],
        y: [`${type}Top`, `${type}Bottom`],
        left: [`${type}Left`],
        right: [`${type}Right`],
        top: [`${type}Top`],
        bottom: [`${type}Bottom`],
    };
};

export const checkIfHasSpacing = (view: AstView, id: string, type: 'margin' | 'padding', side: SpacingSide) => {
    const node = findNodeById(view.tree, id) || null;
    if (!node) {
        return false;
    }

    const keys: Array<StyleCheckerExistence> = getSideMappings(type)[side].map((key) => ({
        existence: true,
        key,
        nonZero: true,
    }));

    return checkForAllStyles(node, keys);
};

export const removePaddingFromNodes = (
    view: AstView,
    setView: (view: AstView) => void,
    nodes: EnrichedAstNode[],
    sides: SpacingSide[]
) => {
    nodes.forEach((node) => {
        removePadding(node, sides);
    });
    setView({...view});
};

export const removeMarginFromNodes = (
    view: AstView,
    setView: (view: AstView) => void,
    nodes: EnrichedAstNode[],
    sides: SpacingSide[]
) => {
    nodes.forEach((node) => {
        removeMargin(node, sides);
    });
    setView({...view});
};

export const findNodeAndRemovePadding = (
    view: AstView,
    setView: (view: AstView) => void,
    id: string,
    sides: SpacingSide[]
) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const node = findNodeById(view.tree, id)!;
    if (!node) {
        return;
    }
    removePadding(node, sides);
    setView({...view});
};

export const findNodeAndRemoveMargin = (
    view: AstView,
    setView: (view: AstView) => void,
    id: string,
    sides: SpacingSide[]
) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const node = findNodeById(view.tree, id)!;
    if (!node) {
        return;
    }
    removeMargin(node, sides);
    setView({...view});
};

export const removePadding = (node: EnrichedAstNode, sides: SpacingSide[]) => {
    if (sides.includes('all') || sides.includes('top') || sides.includes('y')) {
        node.innerPosition.paddingTop = 0;
    }
    if (sides.includes('all') || sides.includes('bottom') || sides.includes('y')) {
        node.innerPosition.paddingBottom = 0;
    }
    if (sides.includes('all') || sides.includes('x') || sides.includes('right')) {
        node.innerPosition.paddingRight = 0;
    }
    if (sides.includes('all') || sides.includes('left') || sides.includes('x')) {
        node.innerPosition.paddingLeft = 0;
    }
};

export const removeMargin = (node: EnrichedAstNode, sides: SpacingSide[]) => {
    if (sides.includes('all') || sides.includes('top') || sides.includes('y')) {
        node.position.marginTop = 0;
    }
    if (sides.includes('all') || sides.includes('bottom') || sides.includes('y')) {
        node.position.marginBottom = 0;
    }
    if (sides.includes('all') || sides.includes('x') || sides.includes('right')) {
        node.position.marginRight = 0;
    }

    if (sides.includes('all') || sides.includes('left') || sides.includes('x')) {
        node.position.marginLeft = 0;
    }
};

export const addValueToCssProperty = (
    view: AstView,
    node: EnrichedAstNode,
    cssKey: CSSProperty,
    value: number,
    unit: Exclude<SizeUnit, 'auto'>
) => {
    const currentStyle = node.styles[cssKey];
    const {unit: currentUnit, value: current} = extractUnitAndValue(currentStyle);

    if (current === 'auto' || currentUnit === 'auto') {
        node.styles[cssKey] = `${value}${unit}`;
    }

    const currentValueInPixels = convertCssUnit({
        key: cssKey,
        toUnit: 'px',
        view,
        nodeId: node.internalId,
        assignNewValueToNode: false,
    });

    let valueInPixels: number;
    // This is a bit of hacky solution to the fact that we made the `convertCssUnit` simpler to use
    // by having it retrieve all the context and values on its own. Technically unit conversion
    // implies the ability to read up the tree for relative units and such. So instead of
    // complicating this, we can just temporarily assign the value to the node in the given style,
    // and run through the conversion function.
    try {
        node.styles[cssKey] = `${value}${unit}`;
        valueInPixels = convertCssUnit({
            key: cssKey,
            toUnit: 'px',
            view,
            nodeId: node.internalId,
            assignNewValueToNode: false,
        });
    } finally {
        // The finally block here is just in case something goes wrong, we don't want the new value to
        // override the old one so we'll just force assign the old one back
        node.styles[cssKey] = currentStyle;
    }

    if (current) {
        const newValueInPixels = currentValueInPixels + valueInPixels;
        node.styles[cssKey] = `${newValueInPixels}px`;
        // We'll convert the new value back to its unit from pixels, and allow the conversion
        // function to assign the value
        return convertCssUnit({
            key: cssKey,
            toUnit: currentUnit,
            view,
            nodeId: node.internalId,
            assignNewValueToNode: true,
        });
    }

    // If there is not current value, then we can just add the value directly.
    node.styles[cssKey] = `${value}${unit}`;
    return;
};

export const moveNodeToFrontOrBack = (view: AstView, node: EnrichedAstNode, position: 'front' | 'back') => {
    const parent = findParentOfNodeById(view.tree, node.internalId);

    // We don't want to support changing z-index for the root node, it has no purpose we handle this at the `condition`
    // level on the right click menu, but we can capture here too in case
    if (!parent) {
        return;
    }

    const siblings = parent.children.filter((n) => node.internalId !== n.internalId);

    // Why do types suck for filter functions????????
    const zIndices = siblings.map((node) => node.zIndex).filter((zIndex) => Number.isInteger(zIndex)) as number[];

    const minZIndex = Math.min(...zIndices) ?? node.zIndex;
    const maxZIndex = Math.max(...zIndices) ?? node.zIndex;

    if (position === 'front') {
        if (node.zIndex === maxZIndex) {
            return;
        }
        node.zIndex = maxZIndex + 1;
    } else {
        if (node.zIndex === minZIndex) {
            return;
        }
        node.zIndex = minZIndex - 1;
    }

    return;
};
